import { useMemo, useState } from "react"
import { CheckIcon, TagIcon } from "@heroicons/react/24/outline"
import type { Filter, Query } from "@cubejs-client/core"
import { toast } from "react-toastify"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import Popover from "~/ui-rtk/components/ui/common/Popover"
import DebouncedInput from "~/ui-rtk/components/ui/controls/DebouncedInput/DebouncedInput"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  useTagAssetProductMutation,
  useUntagAssetProductMutation,
  type AssetProductTag,
} from "~/ui-rtk/api/assetsApi"

/**
 * Per-asset "Save to feed" popover: shows which feed products an asset is
 * tagged into (chips; only MANUAL tags are removable) and a product search
 * over the `mega_feed` cube view so the user can tag it into more.
 *
 * Only meaningful for library assets — callers must gate on a real assetId.
 */

const PREFIX = "mega_feed"
const SEARCH_LIMIT = 20

type FeedProductRow = {
  productRefId: string
  title: string
  imageUrl?: string
}

interface ProductTagPopoverProps {
  /** Library asset id (NOT a synthetic pool id). */
  assetId: string
  /** The asset's current product tags (from the bulk query). */
  productTags: AssetProductTag[]
}

export default function ProductTagPopover({
  assetId,
  productTags,
}: ProductTagPopoverProps) {
  const [term, setTerm] = useState("")

  // Product search over the feed view: title contains OR exact id match, so
  // pasting an item_group_id works as well as typing a name.
  const searchQuery = useMemo<Query>(
    () => ({
      dimensions: [
        `${PREFIX}.item_group_id`,
        `${PREFIX}.parent_title`,
        `${PREFIX}.image_link`,
      ],
      filters: [
        {
          or: [
            {
              member: `${PREFIX}.parent_title`,
              operator: "contains",
              values: [term],
            },
            {
              member: `${PREFIX}.item_group_id`,
              operator: "equals",
              values: [term],
            },
          ],
        },
      ] as Filter[],
      limit: SEARCH_LIMIT,
    }),
    [term],
  )
  const {
    resultSet,
    isLoading: isSearching,
    error: searchError,
  } = useCubeQuery(searchQuery, { skip: !term })

  const results = useMemo<FeedProductRow[]>(() => {
    if (!term || !resultSet) return []
    return (resultSet.rawData() as Record<string, unknown>[])
      .map(row => ({
        productRefId: String(row[`${PREFIX}.item_group_id`] ?? "").trim(),
        title: String(
          row[`${PREFIX}.parent_title`] ?? row[`${PREFIX}.item_group_id`] ?? "",
        ).trim(),
        imageUrl: String(row[`${PREFIX}.image_link`] ?? "").trim() || undefined,
      }))
      .filter(row => row.productRefId)
  }, [term, resultSet])

  const taggedRefIds = useMemo(
    () => new Set(productTags.map(t => t.productRefId)),
    [productTags],
  )

  const [tagAssetProduct, { isLoading: isTagging }] =
    useTagAssetProductMutation()
  const [untagAssetProduct] = useUntagAssetProductMutation()

  const handleTag = async (row: FeedProductRow) => {
    if (taggedRefIds.has(row.productRefId)) return
    try {
      await tagAssetProduct({
        assetId,
        productRefId: row.productRefId,
      }).unwrap()
      toast.success(`Added to ${row.title || row.productRefId}'s feed pool`)
    } catch {
      toast.error("Couldn't tag this asset — try again.")
    }
  }

  const handleUntag = async (tag: AssetProductTag) => {
    try {
      await untagAssetProduct({
        assetId,
        productRefId: tag.productRefId,
      }).unwrap()
    } catch {
      toast.error("Couldn't remove this tag — try again.")
    }
  }

  return (
    <Popover
      relative
      align="end"
      handler={() => (
        <button
          type="button"
          title="Save to a product feed"
          aria-label="Save to a product feed"
          className="w-5 h-5 rounded-full flex items-center justify-center bg-black/70 hover:bg-brand text-white transition"
        >
          <TagIcon className="w-3 h-3" />
        </button>
      )}
    >
      {() => (
        <div
          className="mt-1 w-72 p-3 rounded-lg bg-system-gray-900 border border-system-gray-700/60 shadow-xl flex flex-col gap-2"
          onClick={e => e.stopPropagation()}
        >
          <span className="text-[10px] font-semibold text-system-gray-400 uppercase tracking-wider">
            Product feed
          </span>

          {/* Current tags. Only MANUAL ones carry a remove X — AI / SHOPIFY
              tags are system-derived and not the user's to undo. */}
          {productTags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {productTags.map(tag => (
                <Chip
                  key={tag.productRefId}
                  className="bg-brand/15 text-brand text-[11px] !px-2 !py-0.5"
                  onRemove={
                    tag.source === "MANUAL"
                      ? () => void handleUntag(tag)
                      : undefined
                  }
                >
                  {tag.productName ?? tag.productRefId}
                </Chip>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-system-gray-500">
              Not in any product's feed pool yet.
            </p>
          )}

          <DebouncedInput
            delay={300}
            fullWidth
            placeholder="Search feed products…"
            autoFocus
            className="border-system-gray-700 bg-system-gray-800"
            classes={{ input: "text-white placeholder-system-gray-500" }}
            onChange={e => setTerm(e.target.value.trim())}
          />

          {/* Search results — image + title rows; click to tag. */}
          {term &&
            (searchError ? (
              <p className="text-[11px] text-error">
                Product search is unavailable right now — the feed snapshot
                couldn't be queried.
              </p>
            ) : isSearching ? (
              <div className="flex items-center justify-center py-3">
                <span className="loading loading-spinner loading-xs text-brand" />
              </div>
            ) : !resultSet ? (
              // Not loading, no error, no result: the query was skipped —
              // the company has no mega_feed capability / feed snapshot.
              <p className="text-[11px] text-system-gray-500">
                Product search is unavailable — no feed snapshot for this
                company.
              </p>
            ) : results.length === 0 ? (
              <p className="text-[11px] text-system-gray-500">
                No feed products found
              </p>
            ) : (
              <div className="max-h-56 overflow-y-auto flex flex-col gap-0.5 -mx-1">
                {results.map(row => {
                  const alreadyTagged = taggedRefIds.has(row.productRefId)
                  return (
                    <button
                      key={row.productRefId}
                      type="button"
                      disabled={alreadyTagged || isTagging}
                      onClick={() => void handleTag(row)}
                      className="flex items-center gap-2 px-1 py-1 rounded-md text-left hover:bg-system-gray-800 transition disabled:opacity-60 disabled:hover:bg-transparent"
                    >
                      {row.imageUrl ? (
                        <img
                          src={row.imageUrl}
                          alt=""
                          loading="lazy"
                          className="w-8 h-8 rounded object-cover bg-system-gray-800 shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-system-gray-800 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-white truncate">
                          {row.title || row.productRefId}
                        </span>
                        <span className="block text-[10px] text-system-gray-500 truncate">
                          {row.productRefId}
                        </span>
                      </span>
                      {alreadyTagged && (
                        <CheckIcon className="w-3.5 h-3.5 text-brand shrink-0" />
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
        </div>
      )}
    </Popover>
  )
}
